/**
 * Region Details Modal
 *
 * Shows detailed information about a region when clicked on the knowledge map.
 * Displays completion stats, recent sessions, and quick actions.
 */

import { useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { db } from "@/db/database";
import type { Category } from "@/db/types";

interface RegionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  region: string;
  category: Category;
  granularityLevel: number;
  /** Optional: specific quiz signature to load (if multiple quizzes exist for same region) */
  lastQuizSignature?: string;
}

export function RegionDetailsModal({
  isOpen,
  onClose,
  region,
  category,
  granularityLevel,
  lastQuizSignature,
}: RegionDetailsModalProps) {
  const navigate = useNavigate();

  // Load knowledge state for this region
  // If lastQuizSignature provided, use it for exact match; otherwise use compound key
  const knowledgeState = useLiveQuery(
    () => {
      if (lastQuizSignature) {
        return db.knowledge_state
          .where("lastQuizSignature")
          .equals(lastQuizSignature)
          .first();
      }
      return db.knowledge_state
        .where("[category+region+granularityLevel]")
        .equals([category, region, granularityLevel])
        .first();
    },
    [category, region, granularityLevel, lastQuizSignature]
  );

  // Load quiz config for this region (to get the quiz signature for quick launch)
  const quizConfig = useLiveQuery(async () => {
    if (!knowledgeState) return null;
    return await db.quiz_configs
      .where("signature")
      .equals(knowledgeState.lastQuizSignature)
      .first();
  }, [knowledgeState?.lastQuizSignature]);

  // Load recent quiz sessions for this specific quiz (not all quizzes for the region)
  const recentSessions = useLiveQuery(
    () =>
      db.quiz_sessions
        .where("quizSignature")
        .equals(knowledgeState?.lastQuizSignature || "")
        .reverse()
        .limit(5)
        .toArray(),
    [knowledgeState?.lastQuizSignature]
  );

  // Calculate freshness
  const getFreshnessInfo = () => {
    if (!knowledgeState?.achievedPerfection || !knowledgeState.lastUpdated) {
      return null;
    }

    const daysSince = Math.floor(
      (Date.now() - new Date(knowledgeState.lastUpdated).getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysSince < 7) {
      return { status: "fresh", label: "🔥 Fresh", color: "text-green-600" };
    } else if (daysSince < 14) {
      return { status: "good", label: "✅ Good", color: "text-yellow-600" };
    } else if (daysSince < 30) {
      return { status: "stale", label: "⚠️ Needs Review", color: "text-orange-600" };
    } else {
      return { status: "old", label: "🔴 Practice Needed", color: "text-red-600" };
    }
  };

  const freshnessInfo = getFreshnessInfo();

  const handlePractice = () => {
    if (quizConfig) {
      navigate(`/quiz/play?signature=${quizConfig.signature}`);
      onClose();
    }
  };

  if (!knowledgeState) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-3">
            {region}
            {knowledgeState.achievedPerfection && (
              <Badge variant="default" className="text-xs">
                ✨ Perfect
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {category === "phone" ? "Phone Area Codes" : category === "admin" ? "Administrative Divisions" : "Cities"} • Level {granularityLevel}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Completion Stats */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Completion</span>
              <span className="text-sm font-bold">
                {Math.round(knowledgeState.completionRate)}%
              </span>
            </div>
            <Progress value={knowledgeState.completionRate} className="h-2" />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {knowledgeState.completedPrompts} / {knowledgeState.totalPrompts} items mastered
              </span>
              {freshnessInfo && (
                <span className={freshnessInfo.color}>
                  {freshnessInfo.label}
                </span>
              )}
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg bg-muted/50">
              <div className="text-2xl font-bold">
                {knowledgeState.attemptsCount}
              </div>
              <div className="text-xs text-muted-foreground">Quiz Attempts</div>
            </div>
            <div className="p-4 border rounded-lg bg-muted/50">
              <div className="text-2xl font-bold">
                {knowledgeState.totalPrompts}
              </div>
              <div className="text-xs text-muted-foreground">Total Items</div>
            </div>
          </div>

          {/* Recent Sessions */}
          {recentSessions && recentSessions.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Recent Sessions</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {recentSessions.map((session) => (
                  <div
                    key={session.id}
                    className="p-3 border rounded-lg bg-card text-xs"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold">
                        {session.correctAnswers} / {session.totalQuestions} correct
                      </span>
                      <Badge variant={session.accuracyRate === 100 ? "default" : "secondary"}>
                        {Math.round(session.accuracyRate)}%
                      </Badge>
                    </div>
                    <div className="text-muted-foreground">
                      {new Date(session.completedAt).toLocaleDateString()} •{" "}
                      {Math.round(session.totalTimeMs / 1000 / 60)} min
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button onClick={handlePractice} disabled={!quizConfig} className="flex-1">
              🎯 Practice Now
            </Button>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
